<script setup lang="ts">
import * as LucideIcons from 'lucide-vue-next';

import type { ContentBlock } from '@/types/dynamic-layout';

import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

defineProps<{
  content: ContentBlock;
}>();

// Get Lucide icon component dynamically
function getIconComponent(iconName: string | undefined) {
  if (!iconName) return null;
  return (LucideIcons as any)[iconName] || null;
}

// Format value for display
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return `[${value.length} items]`;
    }
    return JSON.stringify(value, null, 2);
  }
  return String(value);
}

// Format key for display (convert camelCase/snake_case to Title Case)
function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// Check if a component is available
function isComponentAvailable(): boolean {
  // For now, return false as we're using fallback renderers
  // In the future, you could check if the component is registered
  return false;
}

// Format time from seconds to MM:SS
function formatTime(seconds: number): string {
  if (Number.isNaN(seconds) || seconds === undefined || seconds === null) {
    return '00:00';
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}
</script>

<script lang="ts">
// For Markdown support
function markdown(content: string): string {
  // This is a simple implementation. In a real app, you would use a markdown parser
  // like marked.js or markdown-it
  return content
    .replace(/#{3} (.*)$/gm, '<h3>$1</h3>')
    .replace(/#{2} (.*)$/gm, '<h2>$1</h2>')
    .replace(/# (.*)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n\n/g, '<br><br>');
}
</script>

<template>
  <div :class="`mb-4 ${content.className || ''}`">
    <!-- Text Block -->
    <template v-if="content.type === 'text'">
      <p
        v-if="content.data.format === 'plain' || !content.data.format"
        class="leading-relaxed text-sm"
      >
        {{ content.data.content }}
      </p>
      <div
        v-else-if="content.data.format === 'markdown'"
        class="prose dark:prose-invert"
        v-html="markdown(content.data.content)"
      />
      <div v-else-if="content.data.format === 'html'" v-html="content.data.content" />
    </template>

    <!-- Header Block -->
    <template v-else-if="content.type === 'header'">
      <div>
        <component :is="`h${content.data.level || 3}`" class="font-semibold">
          {{ content.data.title }}
        </component>
        <p v-if="content.data.subtitle" class="text-muted-foreground text-sm">
          {{ content.data.subtitle }}
        </p>
      </div>
    </template>

    <!-- Progress Block -->
    <template v-else-if="content.type === 'progress'">
      <div>
        <div class="flex items-center justify-between mb-1.5">
          <span class="text-sm">{{ content.data.label }}</span>
          <span v-if="content.data.rating" class="text-sm font-normal">
            {{ content.data.rating }}
          </span>
          <span v-else-if="typeof content.data.value === 'string'" class="text-sm font-normal">
            {{ content.data.value }}
          </span>
        </div>
        <Progress
          v-if="typeof content.data.value === 'number'"
          :value="
            ((content.data.value || 0) / (content.data.max || content.data.maxValue || 100)) * 100
          "
          :class="content.data.color ? `bg-${content.data.color}-100` : ''"
        />
        <div v-else class="text-sm text-muted-foreground">
          {{ content.data.value }}
        </div>
      </div>
    </template>

    <!-- Tags Block -->
    <template v-else-if="content.type === 'tags'">
      <div class="flex flex-wrap gap-2">
        <!-- Handle both items array and tags array formats -->
        <template v-if="content.data.items">
          <Badge
            v-for="(tag, index) in content.data.items"
            :key="index"
            :variant="tag.variant || 'outline'"
            :class="tag.color ? `border-${tag.color}-500 text-${tag.color}-500` : ''"
            class="font-normal"
          >
            <component
              :is="getIconComponent(tag.icon)"
              v-if="tag.icon && getIconComponent(tag.icon)"
              class="h-3 w-3 mr-1"
            />
            {{ tag.label || tag.text || tag }}
          </Badge>
        </template>
        <template v-else-if="content.data.tags">
          <Badge
            v-for="(tag, index) in content.data.tags"
            :key="index"
            :variant="tag.variant || 'outline'"
            :class="tag.color ? `border-${tag.color}-500 text-${tag.color}-500` : ''"
            class="font-normal"
          >
            <component
              :is="getIconComponent(tag.icon)"
              v-if="tag.icon && getIconComponent(tag.icon)"
              class="h-3 w-3 mr-1"
            />
            {{ tag.label || tag.text || tag }}
          </Badge>
        </template>
      </div>
    </template>

    <!-- Badges Block (alias for tags) -->
    <template v-else-if="content.type === 'badges'">
      <div class="flex flex-wrap gap-2">
        <Badge
          v-for="(badge, index) in content.data.items"
          :key="index"
          :variant="badge.variant || 'outline'"
          :class="badge.color ? `border-${badge.color}-500 text-${badge.color}-500` : ''"
          class="font-normal"
        >
          <component
            :is="getIconComponent(badge.icon)"
            v-if="badge.icon && getIconComponent(badge.icon)"
            class="h-3 w-3 mr-1"
          />
          {{ badge.label || badge.text || badge }}
        </Badge>
      </div>
    </template>

    <!-- Metric Block -->
    <template v-else-if="content.type === 'metric'">
      <div class="text-center">
        <div class="text-3xl font-semibold">
          {{ content.data.value }}
        </div>
        <div class="text-sm text-muted-foreground mt-1">
          {{ content.data.label }}
        </div>
        <div v-if="content.data.trend" class="flex items-center justify-center mt-1">
          <component
            :is="
              getIconComponent(
                content.data.trend.direction === 'up'
                  ? 'ArrowUp'
                  : content.data.trend.direction === 'down'
                    ? 'ArrowDown'
                    : 'Minus',
              )
            "
            class="h-4 w-4 mr-1"
            :class="{
              'text-green-500': content.data.trend.direction === 'up',
              'text-red-500': content.data.trend.direction === 'down',
              'text-gray-500': content.data.trend.direction === 'neutral',
            }"
          />
          <span>{{ content.data.trend.value }}</span>
        </div>
      </div>
    </template>

    <!-- Table Block (simple implementation) -->
    <template v-else-if="content.type === 'table'">
      <div class="overflow-auto">
        <table class="w-full border-collapse">
          <thead>
            <tr>
              <th
                v-for="(column, index) in content.data.columns"
                :key="index"
                class="text-left p-2 border-b font-medium"
              >
                {{ column }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in content.data.rows" :key="rowIndex">
              <td
                v-for="(cell, cellIndex) in row"
                :key="cellIndex"
                class="p-2 border-b border-border"
              >
                {{ cell }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- List Block -->
    <template v-else-if="content.type === 'list'">
      <ul v-if="!content.data.ordered" class="space-y-1 list-disc list-inside">
        <li
          v-for="(item, index) in content.data.items"
          :key="index"
          class="text-sm"
          :class="item.completed ? 'line-through text-muted-foreground' : ''"
        >
          {{ item.text || item }}
        </li>
      </ul>
      <ol v-else class="space-y-1 list-decimal list-inside">
        <li
          v-for="(item, index) in content.data.items"
          :key="index"
          class="text-sm"
          :class="item.completed ? 'line-through text-muted-foreground' : ''"
        >
          {{ item.text || item }}
        </li>
      </ol>
    </template>

    <!-- Grid Block -->
    <template v-else-if="content.type === 'grid'">
      <div :class="content.className || 'grid grid-cols-2 gap-4'">
        <DynamicContentBlock
          v-for="(gridContent, index) in content.data.content"
          :key="gridContent.id || index"
          :content="gridContent"
        />
      </div>
    </template>

    <!-- Custom Component Block with fallback renderer -->
    <template v-else-if="content.type === 'custom'">
      <!-- Try to render the custom component if it exists -->
      <component
        :is="content.data.component"
        v-if="isComponentAvailable()"
        v-bind="content.data.props || {}"
      />
      <!-- Fallback renderer for custom components that don't exist -->
      <div v-else>
        <!-- Handle specific custom component types with fallback renderers -->
        <template v-if="content.data.component === 'SkillsGrid'">
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="skill in content.data.props?.skills || []"
              :key="skill.name"
              class="space-y-1"
            >
              <div class="flex justify-between text-sm">
                <span class="text-muted-foreground">{{ skill.name }}</span>
                <span class="font-medium">{{ skill.score || 0 }}</span>
              </div>
              <Progress :value="skill.score || 0" class="h-2" />
              <div v-if="skill.demonstrated" class="text-xs text-muted-foreground">
                {{ skill.demonstrated }}
              </div>
              <div v-if="skill.shown" class="text-xs text-muted-foreground">
                {{ skill.shown }}
              </div>
              <div v-if="skill.rating" class="text-xs text-muted-foreground">
                Rating: {{ skill.rating }}
              </div>
              <div v-if="skill.maintained" class="text-xs text-muted-foreground">
                {{ skill.maintained }}
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'MetricsGrid'">
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="metric in content.data.props?.metrics || []"
              :key="metric.label"
              class="space-y-1"
            >
              <div class="text-sm text-muted-foreground">
                {{ metric.label }}
              </div>
              <div class="text-lg font-medium">
                <Badge v-if="metric.variant" :variant="metric.variant">
                  {{ metric.value }}
                </Badge>
                <span v-else>{{ metric.value }}</span>
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'CustomerInfo'">
          <div class="space-y-2">
            <div
              v-for="(value, key) in content.data.props || {}"
              :key="key"
              class="flex justify-between"
            >
              <span class="text-sm text-muted-foreground">{{ formatKey(String(key)) }}:</span>
              <span class="text-sm font-medium">{{ formatValue(value) }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'MetadataGrid'">
          <div class="grid grid-cols-2 gap-4">
            <div
              v-for="(value, key) in content.data.props?.data || {}"
              :key="key"
              class="space-y-1"
            >
              <div class="text-xs text-muted-foreground">
                {{ key }}
              </div>
              <div class="text-sm font-medium">
                {{ value }}
              </div>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'ProcessingInfo'">
          <div class="space-y-2">
            <div
              v-for="(value, key) in content.data.props || {}"
              :key="key"
              class="flex justify-between"
            >
              <span class="text-sm text-muted-foreground">{{ formatKey(String(key)) }}:</span>
              <span class="text-sm font-medium">{{ formatValue(value) }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'JourneyInfo'">
          <div class="space-y-2">
            <div
              v-for="(value, key) in content.data.props || {}"
              :key="key"
              class="flex justify-between"
            >
              <span class="text-sm text-muted-foreground">{{ formatKey(String(key)) }}:</span>
              <span class="text-sm font-medium">{{ formatValue(value) }}</span>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'ClosingMetrics'">
          <div class="space-y-2">
            <div class="text-lg font-medium mb-2">
              Score: {{ content.data.props?.score || 0 }}/100
            </div>
            <template v-for="(value, propKey) in content.data.props || {}" :key="propKey">
              <div v-if="String(propKey) !== 'score'" class="flex justify-between">
                <span class="text-sm text-muted-foreground">{{ formatKey(String(propKey)) }}:</span>
                <span class="text-sm font-medium">{{ formatValue(value) }}</span>
              </div>
            </template>
          </div>
        </template>

        <template v-else-if="content.data.component === 'ActionItemsList'">
          <div v-if="content.data.props?.items?.length > 0" class="space-y-2">
            <div
              v-for="(item, index) in content.data.props.items"
              :key="index"
              class="flex items-start gap-2"
            >
              <span class="text-sm">• {{ item }}</span>
            </div>
          </div>
          <div v-else class="text-sm text-muted-foreground">No action items</div>
        </template>

        <template v-else-if="content.data.component === 'ComplianceChecklist'">
          <div class="space-y-2">
            <div
              v-for="item in content.data.props?.items || []"
              :key="item.label"
              class="flex items-center justify-between"
            >
              <span class="text-sm">
                {{ item.label }}
                <Badge v-if="item.required" variant="outline" class="ml-2 text-xs">Required</Badge>
              </span>
              <Badge
                :variant="
                  item.status === 'pass'
                    ? 'default'
                    : item.status === 'fail'
                      ? 'destructive'
                      : 'secondary'
                "
              >
                {{ item.status }}
              </Badge>
            </div>
          </div>
        </template>

        <template v-else-if="content.data.component === 'ViolationsList'">
          <div class="space-y-2">
            <div v-if="content.data.props?.violations?.length > 0">
              <div class="text-sm font-medium text-destructive mb-1">Violations:</div>
              <div
                v-for="(violation, index) in content.data.props.violations"
                :key="index"
                class="text-sm"
              >
                • {{ violation }}
              </div>
            </div>
            <div v-if="content.data.props?.warnings?.length > 0">
              <div class="text-sm font-medium text-warning mb-1">Warnings:</div>
              <div
                v-for="(warning, index) in content.data.props.warnings"
                :key="index"
                class="text-sm"
              >
                • {{ warning }}
              </div>
            </div>
            <div
              v-if="
                (!content.data.props?.violations || content.data.props.violations.length === 0) &&
                (!content.data.props?.warnings || content.data.props.warnings.length === 0)
              "
              class="text-sm text-muted-foreground"
            >
              No violations or warnings
            </div>
          </div>
        </template>

        <template
          v-else-if="
            content.data.component === 'ImprovementsList' ||
            content.data.component === 'TrainingList'
          "
        >
          <div
            v-if="
              content.data.props?.areas?.length > 0 || content.data.props?.suggestions?.length > 0
            "
            class="space-y-2"
          >
            <div
              v-for="(item, index) in content.data.props.areas ||
              content.data.props.suggestions ||
              []"
              :key="index"
              class="text-sm"
            >
              • {{ item }}
            </div>
          </div>
          <div v-else class="text-sm text-muted-foreground">
            No
            {{
              content.data.component === 'ImprovementsList'
                ? 'improvement areas'
                : 'training suggestions'
            }}
            available
          </div>
        </template>

        <template v-else-if="content.data.component === 'TranscriptViewer'">
          <div class="space-y-4 max-h-[600px] overflow-y-auto">
            <div
              v-for="segment in content.data.props?.transcript || []"
              :key="segment.id"
              class="border-b pb-3"
            >
              <div class="flex items-center gap-2 mb-1">
                <Badge :variant="segment.speaker_label === 'spk_0' ? 'default' : 'secondary'">
                  {{ segment.speaker_label === 'spk_0' ? 'Customer' : 'Staff' }}
                </Badge>
                <span class="text-xs text-muted-foreground">
                  {{ formatTime(parseFloat(segment.start_time)) }} -
                  {{ formatTime(parseFloat(segment.end_time)) }}
                </span>
              </div>
              <p class="text-sm">
                {{ segment.transcript }}
              </p>
            </div>
          </div>
        </template>

        <!-- Generic fallback for unknown custom components -->
        <template v-else>
          <div class="space-y-2">
            <div class="text-xs text-muted-foreground">
              Custom Component: {{ content.data.component }}
            </div>
            <div v-if="content.data.props && Object.keys(content.data.props).length > 0">
              <div
                v-for="(value, key) in content.data.props"
                :key="key"
                class="flex justify-between items-start"
              >
                <span class="text-sm text-muted-foreground min-w-[140px]"
                  >{{ formatKey(String(key)) }}:</span
                >
                <div class="text-sm font-medium text-right flex-1">
                  <template v-if="typeof value === 'object' && value !== null">
                    <pre class="bg-muted p-1 rounded text-xs overflow-auto">{{
                      JSON.stringify(value, null, 2)
                    }}</pre>
                  </template>
                  <template v-else>
                    {{ formatValue(value) }}
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- Key-Value Block for displaying field-value pairs -->
    <template v-else-if="content.type === 'keyvalue'">
      <div class="space-y-2">
        <div
          v-for="(item, index) in content.data.items"
          :key="index"
          class="flex justify-between items-start"
        >
          <span class="text-sm text-muted-foreground min-w-[140px]">{{ item.key }}:</span>
          <span class="text-sm font-medium text-right flex-1">
            <Badge v-if="item.badge" :variant="item.badge.variant || 'default'">
              {{ formatValue(item.value) }}
            </Badge>
            <span v-else>{{ formatValue(item.value) }}</span>
          </span>
        </div>
      </div>
    </template>

    <!-- Data Display Block for arbitrary data objects -->
    <template v-else-if="content.type === 'data'">
      <div class="space-y-2">
        <div
          v-for="(value, key) in content.data"
          :key="key"
          class="flex justify-between items-start"
        >
          <span class="text-sm text-muted-foreground min-w-[140px]"
            >{{ formatKey(String(key)) }}:</span
          >
          <div class="text-sm font-medium text-right flex-1">
            <!-- Handle arrays -->
            <template v-if="Array.isArray(value)">
              <div v-if="value.length === 0" class="text-muted-foreground">Empty</div>
              <div v-else class="space-y-1">
                <div v-for="(item, idx) in value" :key="idx" class="ml-2">
                  <span v-if="typeof item === 'object'" class="text-xs">
                    <pre class="bg-muted p-1 rounded text-xs">{{
                      JSON.stringify(item, null, 2)
                    }}</pre>
                  </span>
                  <span v-else>• {{ formatValue(item) }}</span>
                </div>
              </div>
            </template>
            <!-- Handle objects -->
            <template v-else-if="typeof value === 'object' && value !== null">
              <pre class="bg-muted p-2 rounded text-xs overflow-auto max-w-md">{{
                JSON.stringify(value, null, 2)
              }}</pre>
            </template>
            <!-- Handle simple values -->
            <template v-else>
              {{ formatValue(value) }}
            </template>
          </div>
        </div>
      </div>
    </template>

    <!-- Alert/Notice Block -->
    <template v-else-if="content.type === 'alert'">
      <div
        class="p-3 rounded-md border"
        :class="{
          'bg-destructive/10 border-destructive/20': content.data.variant === 'error',
          'bg-warning/10 border-warning/20': content.data.variant === 'warning',
          'bg-primary/10 border-primary/20': content.data.variant === 'info',
          'bg-green-50 border-green-200': content.data.variant === 'success',
          'bg-muted': !content.data.variant || content.data.variant === 'default',
        }"
      >
        <div class="flex items-start gap-2">
          <component
            :is="getIconComponent(content.data.icon || 'AlertCircle')"
            v-if="getIconComponent(content.data.icon || 'AlertCircle')"
            class="h-4 w-4 mt-0.5"
          />
          <div class="flex-1">
            <div v-if="content.data.title" class="font-medium text-sm mb-1">
              {{ content.data.title }}
            </div>
            <div class="text-sm">
              {{ content.data.message }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Fallback for unknown content types - display raw data -->
    <template v-else>
      <div class="p-3 bg-muted/50 rounded-md">
        <div class="text-xs text-muted-foreground mb-1">
          Unknown content type: {{ content.type }}
        </div>
        <pre class="text-xs overflow-auto">{{ JSON.stringify(content.data, null, 2) }}</pre>
      </div>
    </template>
  </div>
</template>
